import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { ActivatedRoute } from '@angular/router';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { DefaultManagementComponent } from 'src/app/controls/forms/default-management.component';
import { HttpError } from 'src/app/core/http-error';
import { RouteData } from 'src/app/core/route-data';
import { Rights } from 'src/app/models/enums/rights';
import { ISmsTemplate } from 'src/app/models/interfaces/sms-template.interface';
import { SmsTemplate } from 'src/app/models/sms-template';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { SmsTemplatesService } from 'src/app/services/sms-templates.service';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { SmsTemplateDetailContext } from './models/sms-template-detail-context';
import { SmsTemplateDetailComponent } from './sms-template-detail/sms-template-detail.component';
import { SmsTemplatesListComponent } from './sms-templates-list/sms-templates-list.component';

enum DisplayMode {
    List,
    Form,
}
@Component({
    selector: 'app-sms-templates',
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, SmsTemplatesListComponent, DetailHeaderComponent, SmsTemplateDetailComponent],
    templateUrl: './sms-templates.component.html',
    styleUrl: './sms-templates.component.scss',
})
export class SmsTemplatesComponent extends DefaultManagementComponent<ISmsTemplate, SmsTemplate, number> implements OnInit {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faEmailsTemplates = faEnvelope;

    /**
     * Liste des enums utilisés dans le template
     */
    public override FormMode = FormMode;
    public override DisplayMode = DisplayMode;

    /**
     * Contexte par défaut du formulaire contenant les données invariantes entre modèles de SMS.
     */
    public defaultContext: SmsTemplateDetailContext = new SmsTemplateDetailContext();

    /**
     * Indique si le bouton d'ajout/Edit doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     * Valeur indiquant s'il s'agit de la gestion des SMS globaux de la souscription.
     */
    public global: boolean;

    /**
     *
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param smsTemplatesService : Service métier utilisé pour gérer les modèles de SMS.
     */
    constructor(
        protected override notificationService: NotificationService,
        private smsTemplatesService: SmsTemplatesService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService,
        private activatedRoute: ActivatedRoute
    ) {
        super(notificationService);

        activatedRoute.data.subscribe((data: RouteData) => {
            this.global = data.global;
        });
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit(): void {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.SmsTemplateCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.SmsTemplateEdit);
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un modèle de SMS.
     * @param smsTemplate Modèle de SMS selectionné.
     */
    public onSmsTemplateSelected(smsTemplate: SmsTemplate) {
        this.spinner.show('main-spinner');
        this.createContext();

        this.loadSmsTemplate(smsTemplate.id).subscribe(
            (x) => {
                this.refresh(SmsTemplate.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le modèle de SMS." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param smsTemplate Modèle de SMS sélectionné.
     */
    public onSmsTemplateDisplayed(smsTemplate: SmsTemplate) {
        this.spinner.show('main-spinner');
        this.createContext();

        this.loadSmsTemplate(smsTemplate.id).subscribe(
            (x) => {
                this.refresh(SmsTemplate.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le modèle de SMS." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param smsTemplate Modèle de SMS sélectionné.
     */
    public onSmsTemplateEdited(smsTemplate: SmsTemplate) {
        this.spinner.show('main-spinner');
        this.createContext();

        // Cas de la mise à jour.
        this.loadSmsTemplate(smsTemplate.id).subscribe(
            (x) => {
                this.refresh(SmsTemplate.createFormCopy(x), FormMode.Edition);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'éditer le modèle de SMS." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onSmsTemplateCreated() {
        this.displayForm(SmsTemplate.createFormCopy(new SmsTemplate()), FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Dupliquer' dans le menu contextuel.
     * @param smsTemplate Modèle de SMS sélectionné.
     */
    public onSmsTemplateDuplicated(smsTemplate: SmsTemplate) {
        let duplicatedSmsTemplate = SmsTemplate.createFormCopy(smsTemplate);
        duplicatedSmsTemplate.id = null;
        this.displayForm(duplicatedSmsTemplate, FormMode.Creation);
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param smsTemplate : Valeur du modèle de SMS retourné par le formulaire.
     */
    public onSmsTemplateUpdated(smsTemplate: SmsTemplate): void {
        this.spinner.show('main-spinner');
        if (this.context.editedItemId) {
            // Cas de la mise à jour.
            this.smsTemplatesService.update(this.context.editedItemId, smsTemplate, this.global).subscribe(
                (x) => {
                    this.refreshWithNotification(SmsTemplate.createFormCopy(x), 'Modèle de SMS mis à jour', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de mettre à jour le modèle de SMS.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            // Cas de la création.
            this.smsTemplatesService.add(smsTemplate, this.global).subscribe(
                (x) => {
                    this.refreshWithNotification(SmsTemplate.createFormCopy(x), 'Modèle de SMS créé', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de créer le modèle de SMS.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param params Template de SMS sélectionné.
     */
    public onSmsTemplateDeleted(smsTemplate: SmsTemplate) {
        this.spinner.show('main-spinner');
        // Suppression effective du client.
        this.smsTemplatesService.delete(smsTemplate.id, this.global).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'Modèle de SMS supprimé');
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de supprimer le modèle de SMS.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lors de l'export
     */
    public onSmsTemplateExported() {
        this.createContext();
        this.itemsList.export('TemplatesSMS.xlsx');
    }

    /**
     * Appellé lors de l'import
     */
    public onSmsTemplateImported() {
        this.itemsList.refresh();
    }

    /**
     * Appellé lors de l'aide
     */
    public onSmsTemplateHelped() {
        this.itemsList.displayHelpDialog('smsTemplates');
    }

    /**
     * Charge les données d'un modèle de SMS.
     * @param smsTemplateId : Id du modèle de SMS à charger.
     */
    public loadSmsTemplate(smsTemplateId: number): Observable<SmsTemplate> {
        return this.smsTemplatesService.get(smsTemplateId);
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param smsTemplate : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    protected fillForm(smsTemplate: SmsTemplate): SmsTemplateDetailContext {
        let context = this.createContext();
        context.formContext.item = smsTemplate;
        context.editedItemId = smsTemplate.id;
        context.global = this.global;
        return context;
    }

    /**
     * Créer un nouveau contexte.
     */
    private createContext(): SmsTemplateDetailContext {
        let context = new SmsTemplateDetailContext();
        return context;
    }
}
